import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Nav from "../components/nav/Nav";
import HeroSection from "../components/hero_section/HeroSection";
import HashtagList from "../components/card/CardHashtags";
import Footer from "../components/footer/Footer";

const SearchArea: React.FC<{ onSearch: (query: string) => void }> = ({ onSearch }) => {
    const [query, setQuery] = useState<string>('');
    const [errorMessage, setErrorMessage] = useState<string>('');
  
    useEffect(() => {
      const placeholder = window.innerWidth <= 640
        ? "Enter topic or idea for hashtag..."
        : "Enter your topic or post idea for hashtag suggestions.";
  
      document.getElementById('searchInput')!.setAttribute('placeholder', placeholder);
    }, []);
  
    const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setQuery(event.target.value);
    };
  
    const handleSubmit = async () => {
      if (query.trim() === '') {
        setErrorMessage('Please enter something you\'re interested in to get popular Rednote hashtag recommendations.');
        return;
      }
      setErrorMessage('');
      onSearch(query);
    };
  
    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === 'Enter') {
        handleSubmit();
      }
    };
  
    return (
      <div className="mb-8">
        <Helmet>
          <title>Discover Popular Xiaohongshu (Rednote) Hashtags in Chinese and English</title>
          <meta charSet="UTF-8"/>
          <meta name="viewport" content="width=device-width, initial-scale=1.0"/>
          <meta name="description" content="Discover XiaoHongShu (Rednote) hashtags for your Xiaohongshu posts. Explore trending categories like challenges, beauty, fashion, food, and more."/>
          <meta name="keywords" content="Xiaohongshu, RedNote App, hashtags, TikTok, Xiaohongshu hashtags, RED, social media, challenges, beauty, fashion, lifestyle, food, content creation"/>
          <meta name="author" content="XiaoHongShu Lingo Team"/>
          <meta property="og:title" content="Discover Popular Xiaohongshu (Rednote) Hashtags in Chinese and English"/>
          <meta property="og:description" content="Discover XiaoHongShu (Rednote) hashtags for your Xiaohongshu posts. Explore trending categories like challenges, beauty, fashion, food, and more."/>
          <meta property="og:url" content="https://xhslingo.com/rednote-hashtag-finder"/>
          <meta property="og:image" content="https://xhslingo.com/logo512.png"/>
          <meta name="twitter:card" content="summary_large_image"/>
          <meta name="twitter:title" content="Discover Popular Xiaohongshu (Rednote) Hashtags in Chinese and English"/>
          <meta name="twitter:description" content="Discover the best XiaoHongShu (Rednote) hashtags for Xiaohongshu posts, from challenges to lifestyle to food."/>
          <meta name="twitter:image" content="https://xhslingo.com/logo512.png"/>
          <meta name="robots" content="index, follow"/>
          <link rel="canonical" href="https://xhslingo.com/rednote-hashtag-finder"/>
          <link href="https://cdnjs.cloudflare.com/ajax/libs/tailwindcss/2.2.19/tailwind.min.css" rel="stylesheet"/>
        </Helmet>
        <textarea
          id="searchInput"
          className="w-full px-4 sm:px-6 py-3 sm:py-4 text-base sm:text-base rounded-lg border-2 border-gray-200 focus:border-red-500 focus:outline-none resize-none min-h-[100px] sm:min-h-[120px]"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          maxLength={100}
        />
        <div className="mt-2 flex justify-between items-center">
          {errorMessage && <p id="errorMessage" className="text-red-500 text-sm">{errorMessage}</p>}
          <p className="text-sm text-gray-500"></p>
        </div>
        <button
          id="submitSearch"
          className="px-6 py-4 bg-red-500 text-white rounded-lg hover:bg-red-600"
          onClick={handleSubmit}
        >
          Discover Top Hashtags
        </button>
      </div>
    );
  };
  interface Hashtag {
    chinese: string;
    english: string;
    pinyin: string;
    description: string;
    usage: string;
    category: string;
  }
  
  const ResultsArea: React.FC<{ hashtags: Hashtag[]; hasSearched: boolean }> = ({ hashtags, hasSearched }) => {
    const renderHashtag = (hashtag: Hashtag) => {
      const createCopyButton = (text: string, tooltipText = 'Copy') => (
        <button
          className="copy-btn ml-2 p-1 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 transition-colors"
          onClick={() => copyToClipboard(text)}
          title={tooltipText}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        </button>
      );
  
      return (
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 hover:shadow-lg transition">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 sm:gap-0">
            <div className="flex-1">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mb-3">
                <div className="flex items-center break-all">
                  <h3 className="text-lg sm:text-xl font-bold">{hashtag.chinese}</h3>
                  {createCopyButton(hashtag.chinese, 'Copy Chinese')}
                </div>
                <div className="flex items-center break-all">
                  <span className="text-blue-600">{hashtag.english}</span>
                  {createCopyButton(hashtag.english, 'Copy English')}
                </div>
              </div>
              <p className="text-gray-600 mb-2 break-words">{hashtag.pinyin}</p>
              <p className="text-gray-700 mb-3 break-words">{hashtag.description}</p>
              <p className="text-sm text-gray-500 break-words">{hashtag.usage}</p>
            </div>
            <div className="sm:ml-4">
              <span className="inline-block bg-red-100 text-red-800 text-sm font-medium px-3 py-1 rounded-full">
                {hashtag.category}
              </span>
            </div>
          </div>
        </div>
      );
    };
  
    const copyToClipboard = (text: string) => {
      navigator.clipboard.writeText(text).then(() => {
        console.log('Copied to clipboard');
      }).catch(err => {
        console.error('Failed to copy:', err);
      });
    };
  
    return (
      <div id="searchResults" className="space-y-4">
        {hashtags.length ? (
          hashtags.map(renderHashtag)
        ) : hasSearched ? ( // 仅在 hasSearched 为 true 时显示提示信息
          <div className="text-left text-gray-500 py-8">
            <p className="mb-4">Oops! We couldn’t find any hashtags matching your query.</p>
            <p className="mb-2">Try entering a different topic or interest to discover trending hashtags! For example:</p>
            <ul className="list-disc list-inside text-gray-400">
              <li>"I love kitty"</li>
              <li>"Healthy recipes"</li>
              <li>"Travel tips"</li>
            </ul>
            <p className="mt-4">Let your imagination flow and find the perfect hashtag!</p>
          </div>
        ) : null}
      </div>
    );
  };



function HashtagFinderPage() {
const [hashtags, setHashtags] = useState<Hashtag[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasSearched, setHasSearched] = useState(false); // 添加 hasSearched 状态


  const fetchHashtags = async (query: string) => {
    setIsLoading(true);
    setHasSearched(true); // 用户点击搜索时，将 hasSearched 设置为 true
    try {
      const response = await fetch(`https://worker1.xhslingo.com/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userInput: query })
      });
      const data = await response.json();
      
      setHashtags(data.length > 0 ? data[0].response.response : []);
    } catch (error) {
      console.error('Error fetching hashtags:', error);
    } finally {
      setIsLoading(false);
    }
  };


    
    return (
      <div >
        <Helmet>
          <meta name="google-adsense-account" content="ca-pub-5419912423604609"></meta>
          <meta charSet="UTF-8"/>
          <meta name="viewport" content="width=device-width, initial-scale=1.0"/>
          <meta name="description" content="Discover XiaoHongShu (Rednote) hashtags for your Xiaohongshu posts. Explore trending categories like challenges, beauty, fashion, food, and more."/>
          <meta name="keywords" content="Xiaohongshu, RedNote App, hashtags, TikTok, Xiaohongshu hashtags, RED, social media, challenges, beauty, fashion, lifestyle, food, content creation"/>
          <meta name="author" content="XiaoHongShu Lingo Team"/>
          <meta property="og:title" content="Discover Popular Xiaohongshu (Rednote) Hashtags in Chinese and English"/>
          <meta property="og:description" content="Discover XiaoHongShu (Rednote) hashtags for your Xiaohongshu posts. Explore trending categories like challenges, beauty, fashion, food, and more."/>
          <meta property="og:url" content="https://xhslingo.com/rednote-hashtag-finder"/>
          <meta property="og:image" content="https://xhslingo.com/logo512.png"/>
          <meta name="twitter:card" content="summary_large_image"/>
          <meta name="twitter:title" content="Discover Popular Xiaohongshu (Rednote) Hashtags in Chinese and English"/>
          <meta name="twitter:description" content="Discover the best XiaoHongShu (Rednote) hashtags for Xiaohongshu posts, from challenges to lifestyle to food."/>
          <meta name="twitter:image" content="https://xhslingo.com/logo512.png"/>
          <meta name="robots" content="index, follow"/>

          <title>Discover Popular Xiaohongshu (Rednote) Hashtags in Chinese and English</title>
          <link rel="canonical" href="https://xhslingo.com/rednote-hashtag-finder"/>
          <link href="https://cdnjs.cloudflare.com/ajax/libs/tailwindcss/2.2.19/tailwind.min.css" rel="stylesheet"/>

            {/* OpenReplay Tracking Script */}
          <script>
            {`
                var initOpts = {
                  projectKey: "DeGMnRMHL9AyHHp5ujvE",
                  defaultInputMode: 0,
                  obscureTextNumbers: false,
                  obscureTextEmails: true,
                };
                var startOpts = { userID: "" };
                (function(A,s,a,y,e,r){
                  r=window.OpenReplay=[e,r,y,[s-1, e]];
                  s=document.createElement('script');s.src=A;s.async=!a;
                  document.getElementsByTagName('head')[0].appendChild(s);
                  r.start=function(v){r.push([0])};
                  r.stop=function(v){r.push([1])};
                  r.setUserID=function(id){r.push([2,id])};
                  r.setUserAnonymousID=function(id){r.push([3,id])};
                  r.setMetadata=function(k,v){r.push([4,k,v])};
                  r.event=function(k,p,i){r.push([5,k,p,i])};
                  r.issue=function(k,p){r.push([6,k,p])};
                  r.isActive=function(){return false};
                  r.getSessionToken=function(){};
                })("//static.openreplay.com/latest/openreplay-assist.js",1,0,initOpts,startOpts);
            `}
          </script>
        </Helmet>

        <Nav />
        <HeroSection
            title="Find the Best XiaoHongShu (Rednote) Hashtags"
            backgroundColor="bg-red-500"  // 可选传递其他颜色
            textColor="text-white"          // 可选传递其他文字颜色
        />
        <div className="container mx-auto px-6 py-12 bg-white max-w-screen-md">
            <SearchArea onSearch={fetchHashtags} />
            {isLoading ? <p>Loading...</p> : <ResultsArea hashtags={hashtags} hasSearched={hasSearched}  />}
        </div>
        <HashtagList />

        <div className="container mx-auto px-6 py-3">
            <p className="text-xl md:text-2xl mb-3">Looking to discover content that matches your interests on Xiaohongshu (Rednote)? Or want to amplify your posts with the right hashtags to reach a larger audience?</p>  
            <p className="text-xl md:text-2xl mb-3">This tool helps you explore the most popular Xiaohongshu (Rednote) hashtags, combining Chinese and English tags to make your experience seamless and impactful.</p>   
        </div>

        <Footer />
        
      </div>
    );
  }
  
export default HashtagFinderPage;